// Group sizes for packages that one payer buys for several people (a family or
// friends sharing a teacher). A package's price is for the whole lesson; each
// size is its own set of lessons, so screens group by size and never compare
// prices across sizes.

import { interpolate } from './i18nUtils'

const sizeOf = (pkg) => pkg.participants || 1
const isTrial = (pkg) => pkg.isTrial ?? pkg.is_trial ?? false
const savingsOf = (pkg) => pkg.perPersonSavings ?? pkg.per_person_savings ?? null

// Regular packages grouped by size, smallest size first. Order inside a group is
// the order given. maxSavings is the group's highest per-person savings, or null.
export function groupPackagesByParticipants(packages) {
  const groups = new Map()
  ;(packages || []).filter(p => !isTrial(p)).forEach(pkg => {
    const size = sizeOf(pkg)
    if (!groups.has(size)) groups.set(size, [])
    groups.get(size).push(pkg)
  })

  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([participants, pkgs]) => {
      const savings = pkgs.map(savingsOf).filter(s => s != null && s > 0)
      return { participants, packages: pkgs, maxSavings: savings.length ? Math.max(...savings) : null }
    })
}

// The group sizes (2 and up) the course sells regular packages for: who can
// come to a trial together.
export function groupSizesSold(packages) {
  return groupPackagesByParticipants(packages).map(g => g.participants).filter(n => n > 1)
}

// { "1": 3, "2": 8 } from the API, as a list the booking screens can render.
export function participantChoices(remainingByParticipants) {
  return Object.entries(remainingByParticipants || {})
    .map(([size, remaining]) => ({ participants: Number(size), remaining }))
    .filter(c => c.remaining > 0)
    .sort((a, b) => a.participants - b.participants)
}

// The smallest group size the course sells that the student holds no lessons
// for, to invite them to learn together. null when there is nothing to offer.
export function upsellGroup(packages, remainingByParticipants) {
  const held = new Set(participantChoices(remainingByParticipants).map(c => c.participants))
  const offer = groupPackagesByParticipants(packages)
    .find(g => g.participants > 1 && !held.has(g.participants))

  return offer
    ? { participants: offer.participants, maxSavings: offer.maxSavings, currency: offer.packages[0]?.currency }
    : null
}

// Live per-person figures for the dashboard package editor (TWD, whole units),
// mirroring CoursePackage#per_person_price / #per_person_savings so the teacher
// sees what students will see before saving. null for a one-person package.
export function perPersonPreview(pkg, editorPackages, defaultPrice) {
  const size = Number(pkg.participants) || 1
  if (size <= 1) return null

  const unitPrice = Number(pkg.unit_price) || 0
  const perPerson = Math.round(unitPrice / size)
  const solo = (editorPackages || []).find(p =>
    (Number(p.participants) || 1) === 1 && !p.is_trial && Number(p.lesson_count) === Number(pkg.lesson_count))
  const baseline = solo ? Number(solo.unit_price) || 0 : Number(defaultPrice) || 0
  const savings = baseline - perPerson

  return { perPerson, savings: savings > 0 ? savings : null }
}

// "1 person" / "2 people": `${key}_one` (or `${key}One`) holds the singular when a
// language needs one; otherwise the plural template takes the count.
export function participantsLabel(count, translations, key, fallback = '%{count} people') {
  const camelOne = `${key}One`
  const one = translations?.[`${key}_one`] ?? translations?.[camelOne]
  if (count === 1 && one) return interpolate(one, { count })
  return interpolate(translations?.[key] || fallback, { count })
}

export const GROUP_SIZES = [2, 3, 4]

// Sections for the dashboard package editor: one-to-one always comes first,
// even with no rows, then each group size that has rows, smallest first.
export function editorGroups(editorPackages) {
  const regular = (editorPackages || []).filter(p => !p.is_trial)
  const sizes = [...new Set([1, ...regular.map(p => Number(p.participants) || 1)])].sort((a, b) => a - b)
  return sizes.map(participants => ({
    participants,
    packages: regular.filter(p => (Number(p.participants) || 1) === participants)
  }))
}

// Sections for the dashboard course card, in the order given inside each one.
// The trial joins one-to-one, since a trial is always one person. Empty
// sections are left out, so a one-size course has a single section.
export function cardSections(packages) {
  const sizeOfRow = (p) => (isTrial(p) ? 1 : Number(p.participants) || 1)
  const sizes = [...new Set((packages || []).map(sizeOfRow))].sort((a, b) => a - b)
  return sizes.map(participants => ({
    participants,
    packages: [...packages.filter(p => isTrial(p) && sizeOfRow(p) === participants),
               ...packages.filter(p => !isTrial(p) && sizeOfRow(p) === participants)]
  }))
}

// Group sizes the teacher can still open for this course.
export function openableGroupSizes(editorPackages) {
  const open = new Set((editorPackages || []).map(p => Number(p.participants) || 1))
  return GROUP_SIZES.filter(n => !open.has(n))
}

// The query key a copied course link uses for the group size, and the one
// PackageSelector reads to open on that size.
export const PARTICIPANTS_LINK_PARAM = 'participants'

// Query params for a course link, opening on `size` when given.
export function courseShareParams(courseId, size = null) {
  return size ? { course: courseId, [PARTICIPANTS_LINK_PARAM]: size } : { course: courseId }
}

// The teacher's booking menu for one student: one entry per group size the
// student holds lessons for, and for a trial one entry per size the course
// sells (a family can come to a trial together). The size shows in the label
// only once a group size is involved, so one-person-only students see the
// same menu as before. Each entry carries the size it books.
export function bookingMenuEntries(balances, courses, t) {
  const items = []
  ;(balances || []).forEach(b => {
    const courseObj = courses?.find(c => c.id === b.team_course_id || String(c.id) === String(b.team_course?.id))
    const courseName = courseObj?.name || b.team_course?.name || t?.unknown_course || 'Unknown Course'
    const cId = b.team_course_id || b.team_course?.id
    if (b.trial_remaining > 0) {
      ;[1, ...groupSizesSold(courseObj?.course_packages)].forEach(n => {
        const trialLabel = t?.trial_label || 'Trial'
        items.push({
          id: cId,
          key: `${cId}-trial-${n}`,
          bookingType: 'trial',
          participants: n,
          // Worded like the regular entries' sizes, so the menu reads one way.
          name: n > 1
            ? `${courseName} - ${participantsLabel(n, t, 'size_label', '%{label} (%{count} people)').replace('%{label}', trialLabel)}`
            : `${courseName} - ${trialLabel}`,
          duration: b.trial_duration_minutes,
          remaining: b.trial_remaining,
        })
      })
    }
    const sizes = participantChoices(b.regular_remaining_by_participants)
    const hasGroupSize = sizes.some(c => c.participants > 1)
    sizes.forEach(({ participants: n, remaining }) => {
      const regularLabel = t?.regular_label || 'Regular'
      const label = hasGroupSize
        ? participantsLabel(n, t, 'size_label', '%{label} (%{count} people)').replace('%{label}', regularLabel)
        : regularLabel
      items.push({
        id: cId,
        key: `${cId}-regular-${n}`,
        bookingType: 'regular',
        participants: n,
        name: `${courseName} - ${label}`,
        duration: b.regular_duration_minutes,
        remaining,
      })
    })
    // Fallback: balance exists but no trial/regular breakdown (legacy data)
    if (b.trial_remaining === 0 && b.regular_remaining === 0 && b.remaining_lessons > 0) {
      items.push({
        id: cId,
        key: `${cId}-regular-1`,
        bookingType: 'regular',
        participants: 1,
        name: `${courseName} - ${t?.regular_label || 'Regular'}`,
        duration: b.regular_duration_minutes || courseObj?.default_duration_minutes,
        remaining: b.remaining_lessons,
      })
    }
  })
  return items
}
